import React, { useMemo, useCallback, useEffect, useState } from 'react';
import {
    useTable,
    useBlockLayout,
    HeaderGroup,
    useSortBy,
    Row,
} from 'react-table';
import { FixedSizeList } from 'react-window';
import * as naturalCompare from 'string-natural-compare';

import {
    DollarsCellStatement,
    PlaysCell,
    TableHeader,
    TableHeaderGroup,
    TableRow,
    TruncatedTextCell,
    TruncatedTwoRowCell,
} from '@trqk/components';
import { getScrollbarWidth } from '@trqk/utils';
// Dimensions in pixels
const rowHeight = 40;
const tableHeight = 355;
const defaultColumnWidth = 80;

interface PerformanceContributionsTableProps {
    /**
     * Current filter value for the table
     */
    filterValue: BroadcastOrDigital;
    /**
     * A timestamp indicating that the table data should be refreshed
     */
    refreshTable: number;
    /**
     * Data to populate the table
     */
    performanceContributions: ProPerformanceContribution[];
}

/* eslint-disable react/jsx-props-no-spreading */
export const PerformanceContributionsTable = ({
    filterValue,
    refreshTable,
    performanceContributions,
}: PerformanceContributionsTableProps): JSX.Element => {
    /* BEGIN UI state logic */

    const [data, setData] = useState(performanceContributions);

    // Reset the data in the table if the filter changes
    useEffect(() => {
        setData(performanceContributions);
    }, [filterValue, performanceContributions]);

    // Reset the data in the table if refreshTable changes
    useEffect(() => {
        setData(performanceContributions);
    }, [refreshTable, performanceContributions]);

    /* END UI state logic */

    /* BEGIN table management logic */

    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const columns = useMemo(
        () => [
            {
                id: 'showName',
                accessor: ({
                    show_name,
                }: ProPerformanceContribution): Maybe<string> =>
                    show_name ?? null,
                Cell: TruncatedTwoRowCell,
                headerLabel: 'Program',
                Header: TableHeader,
                sortType: (
                    rowA: Row<ProPerformanceContribution>,
                    rowB: Row<ProPerformanceContribution>,
                ): number => {
                    const showNameA = rowA?.values?.showName ?? '';
                    const showNameB = rowB?.values?.showName ?? '';

                    return naturalCompare(showNameA, showNameB);
                },
                width: 120,
            },
            {
                id: 'source',
                accessor: ({
                    source_name,
                }: ProPerformanceContribution): Maybe<string> =>
                    source_name ?? null,
                Cell: TruncatedTextCell,
                headerLabel: 'Source',
                Header: TableHeader,
                width: 75,
            },
            {
                id: 'duration',
                accessor: ({
                    duration,
                }: ProPerformanceContribution): Maybe<string> =>
                    duration ?? null,
                headerLabel: 'Length',
                Header: TableHeader,
                width: 60,
            },
            {
                id: 'useName',
                accessor: ({
                    use_name,
                }: ProPerformanceContribution): Maybe<string> => {
                    if (use_name === 'not_reported') {
                        return '—';
                    }

                    return use_name ?? null;
                },
                Cell: TruncatedTextCell,
                headerLabel: 'Use Code',
                Header: TableHeader,
                width: 100,
                tooltipPosition: 'left',
            },
            {
                id: 'plays',
                accessor: ({
                    plays,
                }: ProPerformanceContribution): Maybe<number> => plays ?? null,
                Cell: PlaysCell,
                headerLabel: 'Plays',
                Header: TableHeader,
                width: 55,
            },
            {
                id: 'earnings',
                accessor: ({
                    earnings,
                }: ProPerformanceContribution): number | string =>
                    earnings ?? 'None',
                headerLabel: 'Earnings',
                Header: TableHeader,
                Cell: DollarsCellStatement,
                width: 55,
            },
        ],
        [],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        totalColumnsWidth,
        prepareRow,
    } = useTable<ProPerformanceContribution>(
        {
            columns,
            data,
            defaultColumn,
            initialState: {
                sortBy: [
                    {
                        id: 'showName',
                        desc: false,
                    },
                ],
            },
        },
        useBlockLayout,
        useSortBy,
    );

    /* END table management logic */

    const RenderRow = useCallback(
        ({ index, style }) => {
            const row = rows[index];

            prepareRow(row);

            return (
                <TableRow<ProPerformanceContribution>
                    index={index}
                    style={style}
                    row={row}
                />
            );
        },
        [prepareRow, rows],
    );

    const scrollbarWidth = useMemo(() => getScrollbarWidth(), []);

    return (
        <div {...getTableProps()} className="trqk-table text-sm font-normal">
            <div>
                {headerGroups.map(
                    (
                        headerGroup: HeaderGroup<ProPerformanceContribution>,
                    ): JSX.Element => {
                        const { key: headerGroupKey } =
                            headerGroup.getHeaderGroupProps();

                        return (
                            <TableHeaderGroup<ProPerformanceContribution>
                                key={headerGroupKey}
                                headerGroup={headerGroup}
                            />
                        );
                    },
                )}
            </div>
            <div {...getTableBodyProps()}>
                <FixedSizeList
                    height={tableHeight}
                    itemCount={rows.length}
                    itemSize={rowHeight}
                    width={totalColumnsWidth + scrollbarWidth}
                >
                    {RenderRow}
                </FixedSizeList>
            </div>
        </div>
    );
};
